import { FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { setHasSharedContact, setShowContactShareModal } from '../../reduxToolkit/slices/videoSlice';
import { useSelector } from '../../reduxToolkit/typedRedux';
import { getUserDocuments, shareDataInBooth, shareDataInMeeting } from '../../services/services';
import VyButton from '../common/VyButton';
import VyModal from '../common/VyModal';
import VyTextArea from '../common/VyTextArea';
import VyTextField from '../common/VyTextField';
import { ProfileFileManagement } from './templates/VystemLight/profile/ProfileFileManagement';

interface ContactShareModalProps {
  children?: JSX.Element;
  boothId?: string;
  meetingId?: string;
}

interface FormProps {
  name: string;
  email: string;
  message: string;
}

export const ContactShareModal = ({ children, boothId, meetingId }: ContactShareModalProps) => {
  const [open, setOpen] = useState(false);
  const [attachFiles, setAttachFiles] = useState<boolean>(false);
  const { t } = useTranslation(['booth', 'common']);
  const { register, handleSubmit, control } = useForm<FormProps>();

  const dispatch = useDispatch();

  const user = useSelector(({ common }) => common.user);
  const event = useSelector(({ common }) => common.event);
  const showContactShareModal = useSelector(({ video }) => video.showContactShareModal);
  const hasSharedContact = useSelector(({ video }) => video.hasSharedContact);

  async function sendContactData(props: FormProps) {
    const { data: userFiles } = attachFiles && event && (await getUserDocuments(event._id));

    try {
      if (meetingId) {
        await shareDataInMeeting(meetingId!, props.email, props.name, props.message, attachFiles && userFiles ? userFiles : null);
        dispatch(setHasSharedContact(true));
      } else {
        await shareDataInBooth(boothId!, props.email, props.name, props.message, attachFiles && userFiles ? userFiles : null);
      }
      toast.success(t('successfully-shared'));
      handleClose();
    } catch (exc) {
      console.log(exc);
    }
  }

  function handleClose() {
    if (meetingId) {
      dispatch(setShowContactShareModal(false));
    }

    setOpen(false);
  }

  return (
    <>
      <VyModal open={!hasSharedContact && (open || showContactShareModal)} onClose={handleClose}>
        {(onClose) => (
          <>
            {event && (
              <div>
                <p className="font-bold text-xl">{event?.template?.individualNames?.contactShare}</p>
                <p className="text-black text-opacity-50 text-xs mb-3">{t('send-contact-to-booth-owner')}</p>
                <form onSubmit={handleSubmit(sendContactData)} className="flex flex-col space-y-2">
                  <VyTextField register={register('name')} defaultValue={user?.name} label="Name" size="medium" fullWidth />
                  <VyTextField register={register('email')} defaultValue={user?.email} label="E-Mail" size="medium" fullWidth />
                  <VyTextArea placeholder={t('message')} variant="medium" fullWidth {...register('message')} />
                  {event && event?.settings?.allowUserFileupload && event?.settings?.maxUserFileUpload > 0 && (
                    <>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={attachFiles}
                            onChange={() => setAttachFiles(!attachFiles)}
                            color="primary"
                            name="checkedB"
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                          />
                        }
                        label={t('attach-files')}
                      />
                      {attachFiles && <ProfileFileManagement />}
                    </>
                  )}
                  <div className="flex flex-row justify-between mt-5">
                    <VyButton ghost onClick={onClose}>
                      {t('common:cancel')}
                    </VyButton>
                    <VyButton type="submit" event={event}>
                      {t('common:send')}
                    </VyButton>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </VyModal>
      <div onClick={() => setOpen(true)}>{children}</div>
    </>
  );
};
